import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { analyticsApi } from '../../../api/analytics';
import { exportsApi, saveBlob, ExportError } from '../../../api/exports';
import { projectsApi } from '../../../api/projects';
import { useAuth } from '../../../contexts/AuthContext';
import { PermissionEnum, Project, SummaryResponse } from '../../../types';
import { Button } from '../../../components/ui/atoms/button';
import { Label } from '../../../components/ui/atoms/label';
import { MultiSelect, Loading, EmptyState } from './components';
import { AllocationChart, NonBillableSplitChart, RangeFilter, SummaryCards, TrendChart } from './partials';
import { RangeState } from './interface';
import {
  resolveRange, formatRangeLabel, countWorkingDays, serializeRange, parseRange, parseIdList,
  allEmployees, enabledProjectIds, trendYMax,
} from './utils';

export function AnalyticsPage() {
  const { hasPermission } = useAuth();
  const canView = hasPermission(PermissionEnum.VIEW_ANALYTICS);
  const canExport = hasPermission(PermissionEnum.EXPORT_DATA);

  // The range and the filters live in the URL, so a link opens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo(() => parseRange(searchParams), [searchParams]);
  const resolved = useMemo(() => resolveRange(range), [range]);

  const updateParams = useCallback(
    (changes: Record<string, string | undefined>) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value === undefined) next.delete(key);
          else next.set(key, value);
        }
        return next;
      }, { replace: true }),
    [setSearchParams],
  );

  const setRange = useCallback(
    (next: RangeState) =>
      // Clear every range key first, so switching modes leaves no stale levels behind.
      updateParams({ range: undefined, year: undefined, period: undefined, week: undefined, from: undefined, to: undefined, ...serializeRange(next) }),
    [updateParams],
  );

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // What the URL asked for on arrival. Read once; after that the selections own the state.
  const initialEmployees = useRef(parseIdList(searchParams.get('employees')));
  const initialProjects = useRef(parseIdList(searchParams.get('projects')));

  // The picker lists load regardless of what is selected — gating them on the
  // selection would deadlock: no options means no selection, which means no options.
  const projectsQuery = useQuery<Project[]>({
    queryKey: ['projects-with-members'],
    queryFn: ({ signal }) => projectsApi.listAllWithMembers(signal),
    enabled: canView,
  });
  const projectsList = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);

  // Employees come first: anyone assigned to a project can be picked.
  const employeeOptions = useMemo(() => allEmployees(projectsList), [projectsList]);

  // Start with every project ticked and no employee: the page waits for someone to be
  // picked. Both can come from the URL instead. Seeded once, so deselecting is not undone.
  useEffect(() => {
    if (filtersReady || projectsList.length === 0) return;
    const knownProjects = new Set(projectsList.map((p) => p.id));
    const knownUsers = new Set(employeeOptions.map((o) => o.id));
    const projectsFromUrl = (initialProjects.current ?? []).filter((id) => knownProjects.has(id));
    const usersFromUrl = (initialEmployees.current ?? []).filter((id) => knownUsers.has(id));
    setSelectedProjects(projectsFromUrl.length > 0 ? projectsFromUrl : projectsList.map((p) => p.id));
    setSelectedUsers(usersFromUrl);
    setFiltersReady(true);
  }, [filtersReady, projectsList, employeeOptions]);

  // A project is counted only while a selected employee is assigned to it. A
  // project without one stays listed, greyed out, and keeps its tick so it comes
  // back if that employee is ticked again.
  const enabledProjects = useMemo(
    () => enabledProjectIds(projectsList, selectedUsers),
    [projectsList, selectedUsers],
  );
  const projectOptions = useMemo(
    () => projectsList.map((p) => ({
      id: p.id,
      name: p.name,
      disabled: !enabledProjects.has(p.id),
      hint: 'No selected employee is assigned',
    })),
    [projectsList, enabledProjects],
  );
  const effectiveProjects = useMemo(
    () => selectedProjects.filter((id) => enabledProjects.has(id)),
    [selectedProjects, enabledProjects],
  );

  // Mirror the ticks into the URL. No employee is the default, so it adds nothing; once anyone
  // is picked, they are all written out (everyone included), so a link opens the same view.
  useEffect(() => {
    if (!filtersReady) return;
    updateParams({
      employees: selectedUsers.length === 0 ? undefined : selectedUsers.join(','),
      projects: selectedProjects.length === projectsList.length || selectedProjects.length === 0
        ? undefined : selectedProjects.join(','),
    });
  }, [filtersReady, selectedUsers, selectedProjects, projectsList.length, updateParams]);

  // "Nothing selected" is a real state that must show nothing, so the query is
  // held back rather than silently falling back to "all".
  const nothingSelected = selectedUsers.length === 0 || effectiveProjects.length === 0;
  const canQuery = canView && filtersReady && !nothingSelected;

  // The selected employees are the capacity, so they are sent whenever anything
  // is narrowed — including when only projects are. Ticking everything means no
  // filter, and is sent as none.
  const summaryParams = useMemo(() => {
    const employeesNarrowed = selectedUsers.length !== employeeOptions.length;
    const projectsNarrowed = Array.from(enabledProjects).some((id) => !selectedProjects.includes(id));
    return {
      from: resolved.from,
      to: resolved.to,
      projectId: projectsNarrowed ? effectiveProjects.join(',') : undefined,
      userId: employeesNarrowed || projectsNarrowed ? selectedUsers.join(',') : undefined,
    };
  }, [resolved.from, resolved.to, selectedUsers, selectedProjects, effectiveProjects, enabledProjects, employeeOptions.length]);
  const summaryQuery = useQuery<SummaryResponse>({
    queryKey: ['analytics-summary', summaryParams],
    queryFn: ({ signal }) => analyticsApi.summary(summaryParams, signal).then((r) => r.data),
    enabled: canQuery,
  });

  // Same filters as the cards, so the file matches what is on screen.
  const handleExport = useCallback(async () => {
    setExportError('');
    setIsExporting(true);
    try {
      const file = await exportsApi.analytics(summaryParams);
      saveBlob(file.blob, file.filename);
    } catch (error) {
      setExportError(error instanceof ExportError ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [summaryParams]);

  if (!canView) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        You don&apos;t have permission to view analytics.
      </div>
    );
  }

  const workingDays = countWorkingDays(resolved.from, resolved.to);

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Analytics</h1>

      <div className="flex flex-wrap gap-3 items-end">
        <RangeFilter value={range} onChange={setRange} />

        <div className="space-y-1">
          <Label className="text-xs">Employees</Label>
          <MultiSelect
            label="Employees"
            options={employeeOptions}
            selected={selectedUsers}
            onChange={setSelectedUsers}
            emptyHint="No employees are assigned to any project."
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Projects</Label>
          <MultiSelect
            label="Projects"
            options={projectOptions}
            selected={selectedProjects}
            onChange={setSelectedProjects}
          />
        </div>

        {canExport && (
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleExport}
              disabled={!canQuery || isExporting}
            >
              <Download className="h-4 w-4" /> {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          </div>
        )}
      </div>

      <p className="-mt-3 text-xs text-muted-foreground">
        {formatRangeLabel(resolved.from, resolved.to)} · {workingDays} working {workingDays === 1 ? 'day' : 'days'}
      </p>

      {exportError && <p className="text-sm text-destructive">{exportError}</p>}

      {projectsQuery.isLoading || (!filtersReady && projectsList.length > 0) ? (
        <Loading />
      ) : projectsQuery.isError ? (
        <EmptyState message="Couldn't load projects. Reload the page to try again." />
      ) : nothingSelected ? (
        <EmptyState
          message={
            selectedUsers.length === 0
              ? 'No employees selected. Pick at least one to see data.'
              : selectedProjects.length === 0
                ? 'No projects selected. Pick at least one to see data.'
                : 'None of the selected projects has a selected employee. Pick an enabled project to see data.'
          }
        />
      ) : (
        <>
          <SummaryCards data={summaryQuery.data} isLoading={summaryQuery.isLoading} isError={summaryQuery.isError} />
          <div className="grid gap-4 lg:grid-cols-2">
            <AllocationChart
              data={summaryQuery.data?.allocation}
              isLoading={summaryQuery.isLoading}
              isError={summaryQuery.isError}
            />
            <NonBillableSplitChart
              data={summaryQuery.data?.summary}
              isLoading={summaryQuery.isLoading}
              isError={summaryQuery.isError}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {(['billable', 'effective'] as const).map((metric) => (
              <TrendChart
                key={metric}
                metric={metric}
                data={summaryQuery.data?.trend}
                isLoading={summaryQuery.isLoading}
                isError={summaryQuery.isError}
                yMax={trendYMax(summaryQuery.data?.trend)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
